import { html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import '../product-card/product-card-display';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { BaseComponent } from '../base-component/base-component';
import { PLACEMENT_TOP } from '../../common/constants/bs';
import { PaginationPage } from './in-search/pagination';


/**
 * The base class for pagination components
 *
 * @csspart page-button - Each page button of the pagination.
 * @csspart current-page-button - The current page button.
 * @csspart previous - The previous button arrow.
 * @csspart next - The next button arrow.
 *
 * @event emitAnalytics - to be implemented by the concrete classes, as
 *         each one might use a different name for that event
 */
export class PaginationBase extends BaseComponent {
  /**
   * Overriding base components unloadedHeight
   */
  unloadedHeight = '58px';

  /**
   * The number of pages to show in the pagination. This does not include the first and last pages or the arrows. It should be an odd number.
   */
  @property({ type: Number })
  maxPagesShown = 3;

  /**
   * The page index, starts from 0.
   */
  @property({ type: Number })
  public currentPage = 0;

  @property({ type: Number })
  public total = 0;

  @property()
  public layout: 'horizontal' | 'vertical' = 'horizontal';

  @state()
  private showPrevious = false;

  @state()
  private showNext = false;

  @state()
  private pages: Array<PaginationPage> = [];

  @state()
  private firstPage?: PaginationPage;

  @state()
  private finalPage?: PaginationPage;

  @property({ type: Number })
  public pageLength = 10;

  paginationComponent() {
    return this.total > this.pageLength
      ? html`
          <nav
            role="navigation"
            class="d-flex align-items-center gap-3 flex-column ${this.isLayoutHorizontal() ? 'flex-lg-row' : ''}"
            aria-label="Pagination"
          >
            <span id="paginationPhrase" data-automation-id="paginationPhrase" class="flex-grow-1">
              ${this.showPaginationMsg()}
            </span>
            <ul class="pagination text-center mb-0 gap-1">
              ${this.prevStep()} ${this.firstPageItem()} ${this.pageItems()} ${this.finalPageItem()} ${this.nextStep()}
            </ul>
          </nav>
        `
      : nothing;
  }

  /**
   * It must be implemented per each child class
   * as for different paginations we might
   * have different messages
   */
  showPaginationMsg() {
    throw new Error('Must be implemented by the child class.');
  }

  /**
   * This will be used on the goToPage function,
   * so, it's possible to each child class
   * configure your own analytics event
   * @param pageIndex
   */
  emitAnalytics(pageIndex: number) {
    throw new Error('Must be implemented by the child class.');
  }

  /**
   * it's a hook to be executed by the child pagination
   * when the goToPage is triggered
   */
  goToPageHook(pageIndex?: number): void {
    throw new Error('Must be implemented by the child class.');
  }

  prevStep() {
    return html`
      <li class="page-item ${this.showPrevious ? '' : 'disabled'}">
        <a
          tabindex="${this.currentPage > 0 ? '0' : '-1'}"
          data-bs-toggle="tooltip"
          data-bs-placement=${PLACEMENT_TOP}
          title="Previous Page"
          class="page-link text-dark border-0"
          aria-disabled="${!this.showPrevious}"
          aria-label="Previous Page"
          href="javascript:void(0)"
          id="page-previous"
          data-automation-id="page-previous"
          @click=${() => (this.showPrevious ? this.goToPage(this.currentPage - 1) : null)}
        >
          <uxt-icon icon="chevron-left" size="md"></uxt-icon>
        </a>
      </li>
    `;
  }

  nextStep() {
    return html`
      <li class="page-item ${this.showNext ? '' : 'disabled'}">
        <a
          tabindex="${this.showNext ? '0' : '-1'}"
          data-bs-toggle="tooltip"
          data-bs-html="true"
          title="Next Page"
          class="page-link text-dark border-0"
          aria-disabled="${!this.showNext}"
          data-bs-placement=${PLACEMENT_TOP}
          aria-label="Next Page"
          href="javascript:void(0)"
          id="page-next"
          data-automation-id="page-next"
          @click=${() => (this.showNext ? this.goToPage(this.currentPage + 1) : null)}
        >
          <uxt-icon icon="chevron-right" size="md"></uxt-icon>
        </a>
      </li>
    `;
  }

  firstPageItem() {
    return html`
      ${this.firstPage
        ? html`
            <li class="page-item">
              <a
                class="page-link text-dark border-0"
                href="javascript:void(0)"
                id="page-${this.firstPage.index + 1}"
                data-automation-id="page-${this.firstPage.index + 1}"
                @click=${() => this.goToPage(this.firstPage ? this.firstPage.index : 0)}
                aria-label="Page ${this.firstPage.label}"
              >
                <span class="visually-hidden">Page</span>
                ${this.firstPage.label}
              </a>
            </li>
            <li class="page-item align-self-center disabled">
              <span class="visually-hidden">ellipsis</span>
              <span class="ellipsis" aria-hidden="true">…</span>
            </li>
          `
        : ''}
    `;
  }

  finalPageItem() {
    return html`
      ${this.finalPage
        ? html`
            <li class="page-item align-self-center disabled">
              <span class="visually-hidden">ellipsis</span>
              <span class="ellipsis" aria-hidden="true">…</span>
            </li>
            <li class="page-item">
              <a
                class="page-link text-dark border-0"
                href="javascript:void(0)"
                id="page-${this.finalPage.index + 1}"
                data-automation-id="page-${this.finalPage.index + 1}"
                @click=${() => this.goToPage(this.finalPage ? this.finalPage.index : 0)}
                aria-label="Page ${this.finalPage.label}"
              >
                <span class="visually-hidden">Page</span>
                ${this.finalPage.label}
              </a>
            </li>
          `
        : ''}
    `;
  }

  pageItems() {
    return html`
      ${this.pages.map(
        (page) => html`
          <li class="page-item ${this.currentPage === page.index ? 'active' : ''}">
            <a
              class="page-link text-dark border-0"
              href="javascript:void(0)"
              .disabled=${this.currentPage === page.index}
              id="page-${page.index + 1}"
              data-automation-id="page-${page.index + 1}"
              @click=${() => (this.currentPage !== page.index ? this.goToPage(page.index) : '')}
              aria-label="Page ${page.label}"
              aria-current=${ifDefined(this.currentPage === page.index ? 'page' : undefined)}
            >
              <span class="visually-hidden">Page</span>
              ${page.label}
            </a>
          </li>
        `
      )}
    `;
  }

  goToPage(pageIndex: number) {
    if (this.pageLength) {
      this.goToPageHook(pageIndex);
      this.updatePages();
      this.emitAnalytics(pageIndex);
    }
  }

  protected updatePages() {
    const totalPageCount = Math.ceil(this.total / this.pageLength);

    this.showPrevious = this.currentPage > 0;
    this.firstPage = this.determineFirstPage();
    this.pages = this.mainItems(totalPageCount);
    this.finalPage = this.determineFinalPage(
      this.pages && this.pages.length > 0 ? this.pages[this.pages.length - 1].index : 0,
      totalPageCount
    );
    this.showNext = this.currentPage + 1 < totalPageCount;
  }

  private determineFirstPage() {
    if (this.currentPage - Math.floor(this.maxPagesShown / 2) > 1) {
      return {
        label: '1',
        index: 0,
      };
    } else {
      return undefined;
    }
  }

  private mainItems(totalPageCount: number) {
    const pages: Array<PaginationPage> = [];
    const halfMaxPagesShown = Math.floor(this.maxPagesShown / 2);
    let start = this.currentPage - halfMaxPagesShown;
    let end = this.currentPage + halfMaxPagesShown + 1;

    if (this.maxPagesShown >= totalPageCount) {
      start = 0;
      end = totalPageCount - 1;
    } else if (this.currentPage + halfMaxPagesShown >= totalPageCount - 2) {
      end = totalPageCount - 1;
    } else if (this.currentPage + halfMaxPagesShown >= totalPageCount - 1) {
      start = totalPageCount - this.maxPagesShown;
      end = totalPageCount - 1;
    } else if (this.currentPage - halfMaxPagesShown == 1 && this.maxPagesShown <= totalPageCount) {
      start = 0;
    } else if (this.currentPage - halfMaxPagesShown <= 0 && this.maxPagesShown <= totalPageCount) {
      start = 0;
      end = this.maxPagesShown;
    }

    if (end - start >= 1) {
      for (let i = start; i <= end; i += 1) {
        pages.push({
          label: (i + 1).toString(),
          index: i,
        });
      }
    }

    return pages;
  }

  private determineFinalPage(lastShown: number, totalPageCount: number) {
    if (lastShown + 1 < totalPageCount) {
      return {
        label: totalPageCount.toString(),
        index: totalPageCount - 1,
      };
    } else {
      return undefined;
    }
  }

  private isLayoutHorizontal(): boolean {
    return this.layout === 'horizontal';
  }

  updated() {
    this.bsInit('[data-bs-toggle="tooltip"]', window.bootstrap?.Tooltip);
  }
}
